import pick from "lodash/pick";
import sortBy from "lodash/sortBy";
import isEmpty from "lodash/isEmpty";
import intersection from "lodash/intersection";

import { getDummyStoreTaxonIds } from "@selectors/store";

import {
  CLOUDINARY_ATTACHMENT_HEIGHT,
  CLOUDINARY_ATTACHMENT_WIDTH,
  MENU_ITEM_IMAGE_HEIGHT,
  PREDEFINED_MENU_ITEMS,
  DEFAULT_MENU_ITEM_CODE,
  CATEGORY_SEPARATOR,
} from "@constants/menu";
import { ALL_PRODUCTS, ALL_SUBCATEGORY_CODE } from "@constants/routes";

const DEFAULT_ROOT_LEVEL_ITEM = { children: [], depth: 0, lft: 0, name: "root" };

const DEFAULT_ARR = [];

const filterMenuItemsWithLinkedResource = (orderedMenuItem) => {
  if (orderedMenuItem?.children?.length) {
    orderedMenuItem.children.forEach((child) => {
      filterMenuItemsWithLinkedResource(child);
    });
    //parent level check
    orderedMenuItem.children = orderedMenuItem.children.filter((item) => {
      const { children, linkedTaxonResources, code } = item;
      return (
        children.length > 0 ||
        !isEmpty(linkedTaxonResources) ||
        PREDEFINED_MENU_ITEMS.includes(code)
      );
    });
  }
};

export const getNestedOrderedMenuItems = ({
  menuItems = DEFAULT_ARR,
  includeDisabledMenuItems = false,
  isNewShopCreationFlow = false,
} = {}) => {
  const rootLevelItemId = menuItems.find((item) => item.code === DEFAULT_MENU_ITEM_CODE)?.id;

  const sortedList = sortBy(
    [
      { ...DEFAULT_ROOT_LEVEL_ITEM, id: rootLevelItemId },
      ...menuItems.filter((item) => !item.isDisabled || includeDisabledMenuItems),
    ],
    "depth",
    "lft",
  );

  const depthHash = sortedList.reduce((depthHash, item) => {
    depthHash[item.depth] ??= {};
    depthHash[item.depth][item.id] = {
      ...item,
      children: [],
      allChildTaxonIds: (item.linkedTaxonResources || []).map(({ id }) => id),
    };
    return depthHash;
  }, {});

  let maxDepth = Math.max(...Object.keys(depthHash));
  let totalLevel = maxDepth;

  while (maxDepth > 0) {
    sortedList
      .filter((item) => item.depth === maxDepth)
      .forEach(({ id }) => {
        const item = depthHash[maxDepth][id];
        const depthHashParent = depthHash[maxDepth - 1][item.parentId];
        if (depthHashParent) {
          depthHash[maxDepth][item.id].parent = pick(depthHashParent, "id", "name", "code");
          const inheritedTopContent = item.topContent || depthHashParent.topContent;
          if (inheritedTopContent) item.topContent = inheritedTopContent;
          const inheritedBottomContent = item.bottomContent || depthHashParent.bottomContent;
          if (inheritedBottomContent) item.bottomContent = inheritedBottomContent;
          const inheritedBanner = item.banner || depthHashParent.banner;
          if (inheritedBanner) item.banner = inheritedBanner;
          depthHashParent.children.push(item);
          depthHashParent.forSwag =
            depthHashParent.forSwag || item.linkedTaxonResources?.some((taxon) => taxon.forSwag);
          depthHashParent.isDummyStoreMerch = depthHashParent.forSwag && isNewShopCreationFlow;
          depthHashParent.allChildTaxonIds.push(...item.allChildTaxonIds);
        }
      });
    maxDepth--;
  }

  const orderedMenuItem = {
    ...Object.values(depthHash[0] || {})[0],
    allChildren: menuItems,
    totalLevel,
  };
  filterMenuItemsWithLinkedResource(orderedMenuItem);

  //flatten if only one parent other than all-products is available

  const menuItemChildExceptAllProducts = orderedMenuItem.children.filter(
    (itm) => itm.code !== ALL_PRODUCTS,
  );

  const shouldExpandMenu =
    menuItemChildExceptAllProducts.length === 1 &&
    menuItemChildExceptAllProducts[0].children.length &&
    // should not expand merch category for dummy shops
    !(isNewShopCreationFlow && menuItemChildExceptAllProducts[0].forSwag);

  if (shouldExpandMenu) {
    const parent = menuItemChildExceptAllProducts[0];
    const children = parent.children.map((child) => {
      return { ...child, parent: parent.parent, parentId: parent.parentId, depth: 1 };
    });
    orderedMenuItem.children = [
      ...orderedMenuItem.children.filter((itm) => itm.code === ALL_PRODUCTS),
      ...children,
    ];
  }

  return orderedMenuItem;
};

export const getSelectedMenuItem = (menu, code) => {
  return menu?.children?.find((menuItem) => menuItem.code === code);
};

export const getAllMenuTaxons = (allChildren) => {
  const allChildrenResources = (allChildren || [])
    .filter(
      (category) =>
        !isEmpty(category?.linkedTaxonResources) &&
        !category.isDisabled &&
        !category.isDummyStoreMerch,
    )
    .flatMap((category) => category.linkedTaxonResources);
  const allMenuTaxonIds = allChildrenResources.map((resource) => +resource?.id);
  const withSwag = allChildrenResources.some((resource) => resource?.forSwag);

  return { allMenuTaxonIds, anyMenuWithSwag: withSwag };
};

const checkIfMenuTaxonsExist = (menuItem) =>
  !isEmpty(menuItem.linkedTaxonResources) || PREDEFINED_MENU_ITEMS.includes(menuItem.code);

const checkIfSubMenuExists = (categoryMenuList, subCategory) =>
  subCategory === ALL_SUBCATEGORY_CODE ||
  categoryMenuList.some(
    (menuItem) =>
      menuItem.code === subCategory &&
      (!isEmpty(menuItem.children) || checkIfMenuTaxonsExist(menuItem)),
  );

export const checkIfMenuExists = (menuList, category, subCategory) => {
  if (PREDEFINED_MENU_ITEMS.includes(category)) return true;
  return (
    !isEmpty(menuList) &&
    menuList.some(
      (menuItem) =>
        menuItem.code === category &&
        (!isEmpty(menuItem.children) || checkIfMenuTaxonsExist(menuItem)) &&
        checkIfSubMenuExists(menuItem.children, subCategory),
    )
  );
};

export const getShowOndemandModal = ({ onDemandModalShown, itemType, isBundleShop }) =>
  !onDemandModalShown && itemType === "locker-inventory" && isBundleShop;

export const getSubcategories = (parentCategory) =>
  parentCategory.children.length > 0
    ? [
        { ...parentCategory, name: `All ${parentCategory.name}`, code: ALL_SUBCATEGORY_CODE },
        ...parentCategory.children,
      ]
    : [];

export const replaceCloudinaryDimensions = ({ url, height, width }) => {
  if (!url) return url;

  return url
    .replace(CLOUDINARY_ATTACHMENT_HEIGHT, height || "ih") // if height is not provided, set it to ih (initial height/ original height)
    .replace(CLOUDINARY_ATTACHMENT_WIDTH, width || (height ? "auto" : "iw")); // if width is not provided, check if height is provided, if not, set it to iw (initial width/ original width), if yes set it to automatically calculate the width based on the viewport width
  // see more at https://cloudinary.com/documentation/resizing_and_cropping#setting_the_resize_dimensions
};

export const getMenuItemImage = (url) => {
  if (!url) return url;

  return replaceCloudinaryDimensions({
    url,
    height: MENU_ITEM_IMAGE_HEIGHT,
    width: MENU_ITEM_IMAGE_HEIGHT,
  });
};

export const getParentCategoryFromCategory = (categoryCode = "") => {
  if (categoryCode.includes(CATEGORY_SEPARATOR)) {
    let [parentCategoryCode, activeCategoryCode] = categoryCode.split(CATEGORY_SEPARATOR);
    return {
      parentCategoryCode,
      activeCategoryCode,
    };
  }
  return {
    parentCategoryCode: DEFAULT_MENU_ITEM_CODE,
    activeCategoryCode: categoryCode,
  };
};

export const createMenuCodeMapping = (menu) => {
  const mapping = {};

  function traverse(item) {
    if (!item?.code) return;

    // Add the current item to the mapping
    mapping[item.code] = item;

    // Traverse children recursively
    if (Array.isArray(item.children)) {
      item.children.forEach(traverse);
    }
  }

  // Start traversal from the root item
  traverse(menu);

  return mapping;
};

export const createMenuCodeBreadcrumbNamesMap = (menu) => {
  if (!menu) {
    return {};
  }

  const codeToItem = createMenuCodeMapping(menu);

  const breadcrumbSegmentsForItem = (item) => {
    if (!item?.code) return [];
    const parentCode = item.parent?.code;
    let parentItem = parentCode ? codeToItem[parentCode] : null;

    // Parent may be missing from `codeToItem` when the tree was filtered; use `parent` stub so the chain still builds.
    if (parentCode && !parentItem && item.parent?.name != null) {
      parentItem = {
        code: item.parent.code,
        name: item.parent.name,
        depth: undefined,
        parent: null,
        children: [],
      };
    }

    const segment = {
      code: item.code,
      name: item.name,
      depth: item.depth,
      hasChildren: (item.children?.length ?? 0) > 0,
    };
    if (parentItem) {
      return [...breadcrumbSegmentsForItem(parentItem), segment];
    }
    return item.name ? [segment] : [];
  };

  const mapping = {};

  const traverse = (item) => {
    if (!item?.code) return;
    mapping[item.code] = breadcrumbSegmentsForItem(item);
    if (Array.isArray(item.children)) {
      item.children.forEach(traverse);
    }
  };

  traverse(menu);

  return mapping;
};

export const getTopLevelMenuItem = (code, menuCodeMapping) => {
  const item = menuCodeMapping?.[code];
  if (!item) return null;

  if (item.depth === 1) return item;

  let current = item;
  while (current?.parent?.code && menuCodeMapping[current.parent.code]) {
    const parent = menuCodeMapping[current.parent.code];
    if (parent.depth === 1) return parent;
    current = parent;
  }

  return current;
};

export const getMenuCategoryAndSubCategory = ({ itemData, menuCodeMapping }) => {
  const parentItem = menuCodeMapping[itemData.parent?.code];
  const depth = itemData.depth;

  if (depth === 1) {
    return { category: itemData.code, subCategory: "all" };
  }
  if (depth === 2) {
    return { category: parentItem.code, subCategory: itemData.code };
  }
  const categoryParent = menuCodeMapping[parentItem.code];

  return {
    category: `${categoryParent.parent.code}--${parentItem.code}`,
    subCategory: itemData.code,
  };
};

export const getTaxonIdsRecursively = (category) => {
  if (!category) return [];

  const linkedResources = category.linkedTaxonResources || [];
  const childResources = category.children?.flatMap(getTaxonIdsRecursively) || [];

  return [...linkedResources, ...childResources];
};

export const serializeMenu = ({ data, countryIso, store, isNewShopCreationFlow = false }) => {
  const menuId = data?.menu?.id;
  const dummyStoreTaxons = getDummyStoreTaxonIds({ store });
  const menuItems = (() => {
    const menuItems = data?.menu?.menuItems || [];
    if (isEmpty(dummyStoreTaxons))
      return menuItems.map((item) => ({
        ...item,
        isDisabled: item.menuItemEnabled === false,
      }));

    return menuItems.map((item) => ({
      ...item,
      ...(!isEmpty(item.linkedTaxonResources) && {
        isDisabled: isEmpty(
          intersection(
            dummyStoreTaxons,
            item.linkedTaxonResources.map(({ id }) => +id),
          ),
        ),
      }),
    }));
  })();

  const fetchedOrderedMenuItem = getNestedOrderedMenuItems({
    menuItems,
    isNewShopCreationFlow,
  });

  return {
    ...fetchedOrderedMenuItem,
    countryCode: countryIso,
    id: menuId || fetchedOrderedMenuItem.id,
    fetchedOrderedMenuItemId: fetchedOrderedMenuItem.id,
    skipTitleHumanization: data?.menu?.skipTitleHumanization,
  };
};

export const getProductCountryMapping = (menuItems) =>
  (menuItems || []).reduce((mapping, { linkedResources, country, catalogs }) => {
    const catalogId = catalogs[0]?.store?.id || 0;
    linkedResources?.forEach((resource) => {
      if (resource) {
        mapping[`${resource.id}-${catalogId}`] = country;
      }
    });

    return mapping;
  }, {});

export const flattenMenuChildrenTree = (children = []) =>
  children.flatMap((child) => {
    if (!child) return [];
    return [child, ...(child.children ? flattenMenuChildrenTree(child.children) : [])];
  });
